import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useRoles } from "../../application/hooks/useRoles";
import {
  useRolesList,
  usePermissions as useRolesPermissions,
} from "../../application/hooks/useRolesPermissions";
import { RolesTable } from "../components/RolesTable";
import { ViewAllRolesDialog } from "../components/ViewAllRolesDialog";
import { ViewPermissionsDialog } from "../components/ViewPermissionsDialog";
import { ViewRoleMembersDialog } from "../components/ViewRoleMembersDialog";
import { EditRoleDialog } from "../components/EditRoleDialog";
import { DeleteRoleDialog } from "../components/DeleteRoleDialog";
import type { OldRole as Role } from "../../domain";
import { CreateRoleDialog } from "../components/CreateRoleDialog";
import { PageShell } from "@/components/layout/PageShell";
import { textStyles } from "@/lib/textStyles";
const RolesPermissionsPage = () => {
  const { t } = useTranslation("rolesPermissions");
  const { can } = usePermissions();
  const { isLoading, error, refetch } = useRoles();
  const { roles: rtkRoles, permissions } = useRolesList();
  const { permissions: apiPermissions } = useRolesPermissions();
  console.log("apiPermissions", apiPermissions);
  const [openViewAll, setOpenViewAll] = useState(false);
  const [openViewPermissions, setOpenViewPermissions] = useState(false);
  const [openViewMembers, setOpenViewMembers] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [viewMembersRole, setViewMembersRole] = useState<Role | null>(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [deleteRoleTarget, setDeleteRoleTarget] = useState<Role | null>(null);

  // Map API permissions to grouped format for CreateRoleDialog
  const groupedPermissions =
    apiPermissions?.map((group) => ({
      title: group.category,
      items: group.permissions.map((p) => ({
        id: p.id,
        label: p.generic_name || p.slug,
        description: p.description,
      })),
    })) ?? [];

  // Transform RTK Query roles to OldRole format for UI
  const tableRoles: Role[] = rtkRoles.map((r) => ({
    id: r.id,
    name: r.name,
    access: r.description || "N/A",
    access_level: r.access_level || "-",
    members: r.members_count ?? 0,
    members_count: r.members_count ?? 0,
    permissions: r.permissions?.map((p) => p.id) || [],
    total_permissions: r.total_permissions,
    granted_permissions: r.granted_permissions,
  }));

  const handleViewPermissions = (role: Role) => {
    setSelectedRole(role);
    setOpenViewPermissions(true);
  };

  const handleViewMembers = (role: Role) => {
    setViewMembersRole(role);
    setOpenViewMembers(true);
  };

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setOpenEdit(true);
  };

  const handleDelete = (role: Role) => {
    setDeleteRoleTarget(role);
  };

  const handleViewAllPermissions = () => {
    setSelectedRole(null);
    setOpenViewPermissions(true);
  };

  // Build permissions map for dialogs
  const allPermissionsMap: Record<string, Record<string, string[]>> = {};
  (permissions ?? []).forEach((p) => {
    allPermissionsMap[p.id] = { permissions: [p.name] };
  });

  if (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const status = (error as any)?.status;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiMessage = (error as any)?.data?.error?.message;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isPermissionError =
      status === 403 ||
      (error as any)?.data?.error?.code === "AUTHORIZATION_ERROR";

    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center p-6">
        <div className="rounded-full bg-red-50 p-4">
          <svg
            className="w-8 h-8 text-destructive-text"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>
        {isPermissionError ? (
          <>
            <h3 className="text-base font-semibold text-gray-900">
              {t("accessDenied.title")}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm">
              {apiMessage ?? t("accessDenied.message")}
            </p>
          </>
        ) : (
          <>
            <h3 className="text-base font-semibold text-gray-900">
              {t("loadError.title")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("loadError.message")}
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="mt-2"
            >
              {t("loadError.retry")}
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <PageShell className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-gray-800" style={textStyles.heading2}>
            {t("pageTitle")}
          </h1>
          {isLoading && (
            <span className="text-sm text-gray-500">{t("loading")}</span>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <PermissionGuard allowed={can("role_read")}>
            <Button
              variant="outline"
              onClick={handleViewAllPermissions}
              className="h-10 text-primary border-primary bg-white hover:bg-white cursor-pointer hover:text-primary"
            >
              {t("viewAllPermissions")}
            </Button>
          </PermissionGuard>

          <PermissionGuard allowed={can("role_create")}>
            <Button
              variant="outline"
              onClick={() => setOpenCreate(true)}
              className="h-10 bg-primary border-primary text-white hover:text-white cursor-pointer hover:bg-primary"
            >
              {t("createRole")}
            </Button>
          </PermissionGuard>
        </div>
      </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
        <RolesTable
          roles={tableRoles}
          onViewPermissions={handleViewPermissions}
          onViewMembers={handleViewMembers}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canView={can("role_read")}
          canEdit={can("role_update")}
          canDelete={can("role_delete")}
          canViewMember={can("read_member")}
        />
        </div>
      </div>

      <ViewAllRolesDialog
        open={openViewAll}
        onOpenChange={setOpenViewAll}
        roles={tableRoles}
        permissions={allPermissionsMap}
      />

      <ViewPermissionsDialog
        open={openViewPermissions}
        onOpenChange={setOpenViewPermissions}
        roleId={selectedRole?.id ?? null}
      />

      <ViewRoleMembersDialog
        open={openViewMembers}
        onOpenChange={(open) => {
          setOpenViewMembers(open);
          if (!open) setViewMembersRole(null);
        }}
        role={viewMembersRole}
        canViewMember={can("read_member")}
      />

      <EditRoleDialog
        open={openEdit}
        onOpenChange={setOpenEdit}
        roleId={selectedRole?.id ?? null}
        onSuccess={refetch}
      />
      <DeleteRoleDialog
        open={!!deleteRoleTarget}
        onOpenChange={(open) => !open && setDeleteRoleTarget(null)}
        role={deleteRoleTarget}
        onSuccess={refetch}
      />
      <CreateRoleDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        permissionGroups={groupedPermissions}
        onSuccess={refetch}
      />
    </PageShell>
  );
};

export default RolesPermissionsPage;
